import React, { useState, useRef } from 'react';
import {
  Box,
  Text,
  List,
  ListItem,
  Flex,
  Icon,
  Badge,
  useColorModeValue,
  IconButton,
  Tooltip,
  Divider,
} from '@chakra-ui/react';
import { FiFile, FiAlertTriangle, FiPlay, FiPause } from 'react-icons/fi';
import { getStorageDownloadURL } from '../../../firebase/storage-proxy';
import { NarrationStatus } from '../../../types/narration';

// Methodist brand colors
const methodistBlue = '#0066CC';
const methodistNavy = '#002D72';
const methodistGreen = '#38A169';
const methodistPurple = '#805AD5';

// Status colors - mapped to Methodist brand colors
const statusColors = {
  pending: 'gray.500',
  recording: 'orange.500',
  ready: methodistGreen,
  uploaded: methodistBlue,
  synced: methodistPurple,
  error: 'red.500',
};

interface NarrationListProps {
  narrations: any[];
  selectedNarrationId: string | null;
  onSelect: (id: string) => void;
  conflicts?: string[];
}

const NarrationList: React.FC<NarrationListProps> = ({
  narrations,
  selectedNarrationId,
  onSelect,
  conflicts = [],
}) => {
  const hoverBg = useColorModeValue('gray.100', 'gray.700');
  const selectedBg = useColorModeValue('blue.50', 'blue.900');
  const selectedBorder = methodistBlue;
  
  // Track which audio is currently playing
  const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  // Handle audio playback
  const handlePlayAudio = async (narrationId: string, audioUrl: string | null) => {
    try {
      if (!audioUrl) {
        console.error('[AUDIO] No audio URL provided for narration:', narrationId);
        return;
      }
      
      // If already playing this audio, pause it
      if (playingAudioId === narrationId && audioRef.current) {
        if (!audioRef.current.paused) {
          audioRef.current.pause();
          setPlayingAudioId(null);
          return;
        }
      }
      
      // If playing a different audio, stop it first
      if (audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
      }
      
      // Special case for AI Companion narration which uses a different audio URL format
      let finalAudioUrl = audioUrl;
      
      // If URL is a Firebase Storage URL, get the download URL
      if (audioUrl.startsWith('gs://')) {
        try {
          finalAudioUrl = await getStorageDownloadURL(audioUrl);
        } catch (error) {
          console.error('[AUDIO] Error getting download URL:', error);
          return;
        }
      }
      
      // Play the audio
      if (finalAudioUrl) {
        const audio = new Audio();
        audio.addEventListener('ended', () => {
          setPlayingAudioId(null);
        });
        audio.src = finalAudioUrl;
        await audio.load();
        await audio.play();
        audioRef.current = audio;
        setPlayingAudioId(narrationId);
      }
    } catch (error) {
      console.error('[AUDIO] Error handling audio playback:', error);
    }
  };
  
  // Get status display name and color
  const getStatusInfo = (status: string) => {
    const statusMap: Record<string, { label: string, color: string }> = {
      pending: { label: 'Pending', color: 'gray' },
      recording: { label: 'Recording', color: 'orange' },
      ready: { label: 'Ready', color: 'green' },
      uploaded: { label: 'Uploaded', color: 'blue' },
      synced: { label: 'Synced', color: 'purple' },
      error: { label: 'Error', color: 'red' },
    };
    
    return statusMap[status] || { label: 'Pending', color: 'gray' };
  };
  
  // Get narration ID based on the structure of the narration object
  const getNarrationId = (narration: any): string => {
    // For slide narrations
    if (narration.slideId) return narration.slideId;
    // For dashboard narrations
    if (narration.dashboardId) return narration.dashboardId;
    // Fallback to id property
    return narration.id || '';
  };
  
  // Get narration title with fallback
  const getNarrationTitle = (narration: any, id: string): string => {
    return narration.title || `Slide ${id}`;
  };
  
  // Get narration status with validation
  const getNarrationStatus = (narration: any): NarrationStatus => {
    // Check if status is a valid string and matches expected values
    if (narration.status && typeof narration.status === 'string') {
      // Convert to lowercase for case-insensitive comparison
      const status = narration.status.toLowerCase();
      
      // Check against valid status values
      if (['pending', 'ready', 'error'].includes(status)) {
        return status as NarrationStatus;
      }
      
      // Handle extended status values not in the type definition
      if (['recording', 'uploaded', 'synced'].includes(status)) {
        // Map to closest valid NarrationStatus
        if (status === 'recording') return 'pending';
        if (status === 'uploaded' || status === 'synced') return 'ready';
      }
    }
    
    // Default fallback
    return 'pending';
  };
  
  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Text fontWeight="bold" mb={3} color={methodistNavy}>
        Narrations ({narrations.length})
      </Text>
      
      <Box 
        flex="1" 
        overflow="auto" 
        pr={2}
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
        p={2}
        height="calc(100vh - 180px)"
        css={{
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0, 0.05)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 45, 114, 0.3)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(0, 45, 114, 0.5)',
          },
        }}
      >
        <List spacing={1}>
          {narrations.map((narration) => {
            // Get narration ID using helper function
            const id = getNarrationId(narration);
            const hasConflict = conflicts.includes(id);
            const isSelected = selectedNarrationId === id;
            const hasAudio = !!narration.audioUrl;
            
            // Special check for AI Wellness Companion narration
            const isAICompanion = id === 'ai-companion';
            
            // Show playback button for narrations with 'ready' status even if audioUrl is not immediately available
            const shouldShowPlayback = hasAudio || (isAICompanion && narration.status === 'ready');
            const isPlaying = playingAudioId === id;
            
            // Get validated status and display info
            const rawStatus = narration.status || 'pending';
            const validStatus = getNarrationStatus(narration);
            const statusInfo = getStatusInfo(rawStatus);
            
            // Skip rendering if ID is missing
            if (!id) return null;
            
            return (
              <ListItem 
                key={id}
                p={2}
                borderRadius="md"
                cursor="pointer"
                borderWidth="1px"
                borderColor={isSelected ? selectedBorder : 'transparent'}
                bg={isSelected ? selectedBg : 'transparent'}
                position="relative"
                boxShadow="sm"
                mb={1}
                transition="all 0.2s"
                _hover={{ 
                  bg: hoverBg,
                  boxShadow: 'md'
                }}
                onClick={() => onSelect(id)}
              >
                <Flex direction="column">
                  <Flex align="center" mb={1}>
                    <Icon 
                      as={FiFile} 
                      mr={2} 
                      color={statusColors[rawStatus] || 'gray.500'}
                      boxSize={4}
                    />
                    <Text 
                      fontWeight={isSelected ? 'bold' : 'semibold'} 
                      fontSize="sm"
                      color={methodistNavy}
                      flex="1"
                      noOfLines={1}
                    >
                      {getNarrationTitle(narration, id)}
                    </Text>
                    
                    {/* Clean, minimalist playback button for ready narrations */}
                    {shouldShowPlayback && (
                      <Tooltip label={isPlaying ? "Pause" : "Play Audio"}>
                        <IconButton
                          aria-label={isPlaying ? "Pause" : "Play Audio"}
                          icon={<Icon as={isPlaying ? FiPause : FiPlay} />}
                          size="xs"
                          variant="ghost"
                          colorScheme={rawStatus === 'error' ? 'red' : 'blue'}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlayAudio(id, narration.audioUrl);
                          }}
                        />
                      </Tooltip>
                    )}
                  </Flex>
                  
                  <Flex align="center" justify="space-between">
                    <Badge 
                      colorScheme={statusInfo.color} 
                      variant="subtle" 
                      fontSize="xs"
                    >
                      {statusInfo.label}
                    </Badge>
                    
                    {hasConflict && (
                      <Tooltip label="This narration has conflicts">
                        <Icon 
                          as={FiAlertTriangle} 
                          color="orange.500" 
                          boxSize={3} 
                          ml={1}
                        />
                      </Tooltip>
                    )}
                  </Flex>
                </Flex>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

export default NarrationList;
